import { Injectable, Injector } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { User } from '../../interfaces/routering/user';
import { Organisation } from '../../interfaces/routering/organisation';
import { LocalStorageService } from './local-storage.service';
import { Router } from '@angular/router';
import { ServerResponse } from '../../interfaces/base/server.response';
import { ApiEndpointsService } from './api-endpoints.service';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import * as Sentry from '@sentry/browser';
import * as _ from 'lodash';
import { catchError, map, mergeMap, retryWhen } from 'rxjs/operators';
import { MsalAuthService } from './msal-auth.service';
import * as moment from 'moment';

@Injectable()

export class AuthenticationService {
  private msalAuthService: MsalAuthService;

  private _user: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public user$: Observable<User> = this._user as Observable<User>;

  private lastUserValidation: number = null;
  private readonly VALIDATION_INTERVAL: number = 60 * 60 * 1000; // revalidate user every hour
  private validationTimer: any;

  private _organisation: BehaviorSubject<Organisation> = new BehaviorSubject<Organisation>(null);
  public organisation$: Observable<Organisation> = this._organisation as Observable<Organisation>;

  public returnUrl: string = '/';

  get currentUser(): User | null {
    return this._user.value;
  }

  constructor(private api: ApiService,
              private httpBackend: HttpBackend,
              private apiEndpoints: ApiEndpointsService,
              private router: Router,
              private localStorageService: LocalStorageService,
              private injector: Injector,
  ) {
    setTimeout((): void => {
      this.msalAuthService = this.injector.get(MsalAuthService);
    });

    this._organisation.next(this.localStorageService.get('default-organisation'));
    this.getDefaultOrganisation();

    this.organisation$.subscribe((organisation: Organisation): void => {
      this.updateAppColors(organisation);
    });

    this.user$.subscribe((user: User): void => {
      if (user) {
        Sentry.setUser({
          id: user.id,
          username: user.name,
          email: user.email,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    document.addEventListener('visibilitychange', (): void => {
      if (!document.hidden && this.currentUser && this.shouldRevalidate()) {
        this.doNextValidation();
      }
    });
  }

  updateOrganisation(organisation: Organisation): void {
    this.localStorageService.set('default-organisation', organisation);
    this._organisation.next(organisation);
  }

  updateAppColors(organisation: Organisation = null): void {
    document.documentElement.style.setProperty('--menu-background-color',
      (organisation !== null && typeof organisation.color_menu_background !== 'undefined' && organisation.color_menu_background !== ''
          ? organisation.color_menu_background
          : '#005192'
      )
    );
    document.documentElement.style.setProperty('--menu-text-color',
      (organisation !== null && typeof organisation.color_menu_text !== 'undefined' && organisation.color_menu_text !== ''
          ? organisation.color_menu_text
          : '#ffffff'
      )
    );
    document.documentElement.style.setProperty('--primary-color',
      (organisation !== null && typeof organisation.color_primary_action !== 'undefined' && organisation.color_primary_action !== ''
          ? organisation.color_primary_action
          : '#1b143c'
      )
    );
    document.documentElement.style.setProperty('--primary-color-text',
      (organisation !== null && typeof organisation.color_primary_action_text !== 'undefined' && organisation.color_primary_action_text !== ''
          ? organisation.color_primary_action_text
          : '#ffffff'
      )
    );
    document.documentElement.style.setProperty('--secondary-color',
      (organisation !== null && typeof organisation.color_secondary_action !== 'undefined' && organisation.color_secondary_action !== ''
          ? organisation.color_secondary_action
          : '#666666'
      )
    );
    document.documentElement.style.setProperty('--secondary-color-text',
      (organisation !== null && typeof organisation.color_secondary_action_text !== 'undefined' && organisation.color_secondary_action_text !== ''
          ? organisation.color_secondary_action_text
          : '#ffffff'
      )
    );
  }

  getDefaultOrganisation(): void {
    const httpApi: HttpClient = new HttpClient(this.httpBackend);
    httpApi.get(environment.api_endpoint + this.apiEndpoints.get('public.default-organisation'), {
      observe: 'response',
      responseType: 'json',
    }).subscribe((response: HttpResponse<ServerResponse>): void => {
      if (typeof response.body.data !== 'undefined') {
        this.localStorageService.set('default-organisation', response.body.data);
        this._organisation.next(response.body.data);
      }
    });
  }

  login(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.login'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.token !== 'undefined' && typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token', response.data.token);
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);

            if (typeof response.data.organisation !== 'undefined') {
              this.localStorageService.set('default-organisation', response.data.organisation);
              this._organisation.next(response.data.organisation);
            }

            this.lastUserValidation = Date.now();
            this.scheduleNextValidation();

            this.router.navigateByUrl(this.returnUrl).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  logout(): Subscription {
    return this.api.get(this.apiEndpoints.get('auth.logout')).subscribe((): void => {
      this.silentLogout();
    });
  }

  silentLogout(): void {
    this.localStorageService.delete('api-token');
    this.localStorageService.delete('api-token-impersonate');
    this.localStorageService.delete('user');
    this.localStorageService.delete('organisation');

    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.lastUserValidation = null;

    // because we're using an older module, we can't use clearCache() and need to clear data manually
    if (!environment.production || this._user.value && this._user.value.role === 'super-admin') {
      console.log('Auth: clearing local storage');
    }
    Object.keys(localStorage).forEach((key: string): void => {
      if (key.startsWith('msal.') || key.includes('login.windows.net')) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach((key: string): void => {
      if (key.startsWith('msal.') || key.includes('login.windows.net')) {
        sessionStorage.removeItem(key);
      }
    });

    this._user.next(null);
    this._organisation.next(null);

    // not sure why, but setTimeout is required, otherwise it does not redirect
    setTimeout((): void => {
      this.router.navigateByUrl('/auth/login').then((): void => {
      });
    }, 0);
  }

  validateCompleteAccountToken(id: number, expires: string, signature: string): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.complete-account', {':id': id, ':expires': expires, ':signature': signature}), null, true);
  }

  completeAccount(id: number, expires: string, signature: string, formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.complete-account', {':id': id, ':expires': expires, ':signature': signature}), formData);
  }

  forgotPassword(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.forgot-password'), formData);
  }

  validateResetPasswordToken(token: string, email: string): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.reset-password.validate', {':token': token, ':email': email}), null, true);
  }

  resetPassword(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.reset-password'), formData);
  }

  changePassword(formData: any): Observable<ServerResponse> {
    return this.api.post(this.apiEndpoints.get('auth.change-password'), formData);
  }

  getAccount(): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.account'), null, true);
  }

  getUserAccount(): Observable<boolean> {
    return this.api.get(this.apiEndpoints.get('auth.account'), null, true).pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error: HttpErrorResponse, i: number) => {
            const retryAttempt: number = i + 1;
            if (error.status === 504 && retryAttempt <= 3) {
              if (!environment.production) {
                console.log('Auth: 504 received from SW, retry attempt ' + retryAttempt);
              }
              return of(error);
            }

            return throwError(error);
          })
        )
      ),
      map((response: ServerResponse): boolean => {
        if (!environment.production) {
          console.log('Auth: received server response', response);
        }
        if (typeof response.data !== 'undefined') {
          if (!environment.production) {
            console.log('Auth: user is validated');
          }
          if (this.differentThanLocalAccount(response.data)) {
            this.localStorageService.set('user', response.data);

            // when we get an updated user's profile, check if user is external contractor and if user agreed with agreement
            this.didUserAgreeToContractorAgreement(response.data);
          }

          this._user.next(response.data);

          if (typeof response.data.organisation_switched !== 'undefined') {
            this.localStorageService.delete('default-organisation');
            this.localStorageService.delete('organisation');
          }

          this.lastUserValidation = Date.now();
          this.scheduleNextValidation();

          return true;
        } else {
          if (!environment.production) {
            console.log('Auth: user validation failed');
          }
          return false;
        }
      }),
      catchError(() => {
        if (!environment.production) {
          console.log('Auth: user validation failed');
        }
        this.silentLogout();
        return of(false);
      })
    );
  }

  getUserFromLocalStorage(): User | null {
    return this.localStorageService.get('user');
  }

  updateAccount(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.account'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (this.differentThanLocalAccount(response.data)) {
            this.localStorageService.set('user', response.data);
            this._user.next(response.data);
          }

          this.router.navigate(['/']).then((): void => {
          });

          return true;
        } else {
          return false;
        }
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  differentThanLocalAccount(data: User): boolean {
    const localAccount: User = this.localStorageService.get('user');

    return (
      !localAccount ||
      !_.isEqual(
        _.omit(localAccount, ['last_access_at']),
        _.omit(data, ['last_access_at'])
      )
    );
  }

  startImpersonating(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.impersonate'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.token !== 'undefined' && typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token-impersonate', this.localStorageService.get('api-token'));
            this.localStorageService.set('api-token', response.data.token);
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);

            this.router.navigate(['/']).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  stopImpersonating(): Observable<boolean> {
    return this.api.delete(this.apiEndpoints.get('auth.impersonate'), false).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (typeof response.data.user !== 'undefined') {
            this.localStorageService.set('api-token', this.localStorageService.get('api-token-impersonate'));
            this.localStorageService.delete('api-token-impersonate');
            this.localStorageService.set('user', response.data.user);
            this._user.next(response.data.user);

            this.router.navigate(['/routering/gebruikers']).then((): void => {
            });

            return true;
          }
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  isImpersonating(): boolean {
    return this.localStorageService.get('api-token-impersonate') !== null;
  }

  resendInvitation(id: number): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('routering.users.resend-invitation', {':id': id}));
  }

  getLatestContractorAgreement(): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.contractor-agreement'));
  }

  didUserAgreeToContractorAgreement(user: User): void {
    if (user && typeof user.is_external_contractor !== 'undefined') {
      if (user.is_external_contractor && !user.external_contractor_agreement_id) {
        this.router.navigate(['/auth/verwerkersovereenkomst']).then((): void => {
        });
      }
    }
  }

  agreeToContractorAgreement(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.contractor-agreement'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (!this.api.isErrorResponse(response)) {
          this.localStorageService.set('user', response.data.user);
          this._user.next(response.data.user);

          this.router.navigate(['/']).then((): void => {
          });
          return true;
        }

        return false;
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  getAvailability(): Observable<ServerResponse> {
    return this.api.get(this.apiEndpoints.get('auth.availability'), null, true);
  }

  updateAvailability(formData: any): Observable<boolean> {
    return this.api.post(this.apiEndpoints.get('auth.availability'), formData).pipe(
      map((response: ServerResponse): boolean => {
        if (typeof response.data !== 'undefined') {
          if (this.differentThanLocalAccount(response.data)) {
            this.localStorageService.set('user', response.data);
            this._user.next(response.data);
          }

          this.router.navigate(['/']).then((): void => {
          });

          return true;
        } else {
          return false;
        }
      }),
      catchError((): Observable<boolean> => of(false))
    );
  }

  scheduleNextValidation(): void {
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }

    if (!environment.production || this._user.value && this._user.value.role === 'super-admin') {
      console.log('Auth: scheduling next validation at ' + moment().add(this.VALIDATION_INTERVAL / 1000, 'seconds').format('Y-MM-DD HH:mm:ss'));
    }
    this.validationTimer = setTimeout((): void => {
      if (!environment.production || this._user.value && this._user.value.role === 'super-admin') {
        console.log('Auth: validation timed out, re-validate user.');
      }

      this.doNextValidation();
    }, this.VALIDATION_INTERVAL);
  }

  doNextValidation(): void {
    // first, we need to check if user used MSAL/Entra login because if he did, we need to refresh token first
    const token: string = this.localStorageService.get('api-token');
    if (token && token.split('.').length === 3) {
      if (!environment.production || this._user.value && this._user.value.role === 'super-admin') {
        console.log('Auth: this is MSAL login, refreshing token...');
      }
      this.msalAuthService.acquireTokenSilent();
    } else {
      if (!environment.production || this._user.value && this._user.value.role === 'super-admin') {
        console.log('Auth: this is a normal login, validating user...');
      }
      this.getUserAccount().subscribe((isValid: boolean): void => {
        if (!isValid) {
          this.silentLogout();
          this.router.navigateByUrl('/').then((): void => {
          });
        }

      });
    }
  }

  shouldRevalidate(): boolean {
    if (!this.lastUserValidation) {
      return true;
    }

    return (Date.now() - this.lastUserValidation) > this.VALIDATION_INTERVAL;
  }
}
